<template>
  <div class="llms-container">
    <!-- Top Header -->
    <div class="page-header">
      <h1>Models</h1>
      <button 
        v-if="!showCreateForm" 
        @click="showCreateForm = true" 
        class="btn btn-primary"
      >
        <i class="icon-plus"></i> Add Model
      </button>
    </div>
    
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>
    
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Panel: Providers List -->
      <div class="left-panel">
        <div class="panel-header">
          <h3>Providers</h3>
          <span class="provider-count">{{ Object.keys(groupedModels).length }} providers</span>
        </div>
        
        <div class="providers-list">
          <div 
            v-for="(models, provider) in groupedModels" 
            :key="provider" 
            class="provider-item"
            :class="{ 'active': selectedProvider === provider }"
            @click="selectProvider(provider)"
          >
            <div class="provider-avatar">
              <i :class="getProviderIcon(provider)"></i>
            </div>
            <div class="provider-info">
              <h4>{{ getProviderName(provider) }}</h4>
              <p>{{ models.length }} models</p>
              <div class="provider-meta">
                <span class="active-count">{{ models.filter(m => m.is_active).length }} active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right Panel: Models/Form -->
      <div class="right-panel">
        <!-- Create/Edit Model Form -->
        <div v-if="showCreateForm || editingModel" class="model-form-panel">
          <div class="panel-header">
            <h3>{{ editingModel ? 'Edit Model' : 'Add New Model' }}</h3>
            <button @click="closeForm" class="btn-close">
              <i class="icon-close"></i>
            </button>
          </div>
          
          <form @submit.prevent="editingModel ? updateModel() : createModel()" class="model-form">
            <div class="form-row">
              <div class="form-group">
                <label>Model Name *</label>
                <input
                  v-model="modelForm.name"
                  type="text"
                  placeholder="Enter model name"
                  required
                >
              </div>
              <div class="form-group">
                <label>Provider *</label>
                <select v-model="modelForm.provider" @change="onProviderChange" required>
                  <option value="">Select Provider</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="gemini">Gemini</option>
                <option value="groq">Groq</option>
                  <option value="local">Local</option>
                </select>
              </div>
            </div>
            
            <div class="form-group" v-if="modelForm.provider && availableModels.length > 0">
              <label>Model ID *</label>
              <select v-model="modelForm.model_id" required>
                <option value="">Select Model</option>
                <option v-for="model in availableModels" :key="model.model_id" :value="model.model_id">
                  {{ model.name }} ({{ model.model_id }})
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label>API Key *</label>
              <input
                v-model="modelForm.api_key"
                type="password"
                placeholder="Enter API key"
                required
              >
            </div>
            
            <div class="form-group">
              <label>Endpoint (optional)</label>
              <input
                v-model="modelForm.endpoint"
                type="url"
                placeholder="Custom endpoint URL"
              >
            </div>
            
            <!-- Advanced Settings -->
            <div class="settings-section">
              <h4>Settings</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Temperature ({{ modelForm.temperature }})</label>
                  <input 
                    type="range" 
                    v-model.number="modelForm.temperature" 
                    min="0" 
                    max="1" 
                    step="0.1"
                    class="range-input"
                  >
                </div>
                <div class="form-group">
                  <label>Max Tokens</label>
                  <input 
                    type="number" 
                    v-model.number="modelForm.max_tokens" 
                    min="1" 
                    max="8000"
                    placeholder="1000"
                  >
                </div>
              </div>
              
              <div class="form-group checkbox-group">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="modelForm.system_level">
                  <span>System Level Model</span>
                </label>
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeForm" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingModel ? 'Update Model' : 'Add Model' }}
              </button>
            </div>
          </form>
        </div>
        
        <!-- Models List -->
        <div v-else-if="selectedProvider" class="models-panel">
          <div class="panel-header">
            <div class="provider-title">
              <h3>{{ getProviderName(selectedProvider) }} Models</h3>
              <span class="model-count-badge">{{ selectedModels.length }} models</span>
            </div>
            <div class="header-actions">
              <button @click="showCreateForm = true; modelForm.provider = selectedProvider" class="btn btn-primary">
                <i class="icon-plus"></i> Add Model
              </button>
            </div>
          </div>
          
          <!-- Provider Overview -->
          <div class="provider-overview">
            <div class="provider-stats">
              <div class="stat-item">
                <span class="label">Total Models:</span>
                <span class="value">{{ selectedModels.length }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Active:</span>
                <span class="value status active">{{ selectedModels.filter(m => m.is_active).length }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Inactive:</span>
                <span class="value status inactive">{{ selectedModels.filter(m => !m.is_active).length }}</span>
              </div>
            </div>
          </div>
          
          <!-- Models Grid -->
          <div class="models-section">
            <div v-if="selectedModels.length === 0" class="no-models">
              <i class="icon-model"></i>
              <h4>No models found</h4>
              <p>Add your first {{ getProviderName(selectedProvider) }} model to get started</p>
              <button @click="showCreateForm = true; modelForm.provider = selectedProvider" class="btn btn-primary">
                <i class="icon-plus"></i> Add Model
              </button>
            </div>
            
            <div v-else class="models-grid">
              <div 
                v-for="model in selectedModels" 
                :key="model.id" 
                class="model-card"
                :class="{ 'active': model.is_active }"
              >
                <div class="model-header">
                  <div class="model-icon">
                    <i :class="getProviderIcon(model.provider)"></i>
                  </div>
                  <div class="model-actions">
                    <button @click="editModel(model)" class="btn-icon" title="Edit">
                      <i class="icon-edit"></i>
                    </button>
                    <button @click="testModel(model)" class="btn-icon" title="Test">
                      <i class="icon-test"></i>
                    </button>
                    <button @click="deleteModel(model.id)" class="btn-icon danger" title="Delete">
                      <i class="icon-trash"></i>
                    </button>
                  </div>
                </div>
                
                <div class="model-content">
                  <h4>{{ model.name }}</h4>
                  <p class="model-id">{{ model.model_id }}</p>
                  
                  <div class="model-meta">
                    <div class="meta-row">
                      <span class="meta-label">Status:</span>
                      <span class="meta-value status" :class="{ 'active': model.is_active, 'inactive': !model.is_active }">
                        {{ model.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                    <div class="meta-row" v-if="model.max_tokens">
                      <span class="meta-label">Max Tokens:</span>
                      <span class="meta-value">{{ model.max_tokens.toLocaleString() }}</span>
                    </div>
                    <div class="meta-row" v-if="model.temperature">
                      <span class="meta-label">Temperature:</span>
                      <span class="meta-value">{{ model.temperature }}</span>
                    </div>
                    <div class="meta-row" v-if="model.endpoint">
                      <span class="meta-label">Endpoint:</span>
                      <span class="meta-value endpoint">{{ truncateEndpoint(model.endpoint) }}</span>
                    </div>
                  </div>
                  
                  <div v-if="model.system_level" class="system-badge">
                    System Level
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Welcome Panel -->
        <div v-else class="welcome-panel">
          <div class="welcome-content">
            <i class="icon-model-large"></i>
            <h3>Welcome to Models</h3>
            <p>Manage your language models across different providers. Add API keys, configure settings, and test your models all in one place.</p>
            <button @click="showCreateForm = true" class="btn btn-primary">
              <i class="icon-plus"></i> Add Your First Model
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'LLMsView',
  setup() {
    const store = useStore()
    const models = computed(() => {
      const rawModels = store.state.llm?.items || []
      return Array.isArray(rawModels) ? rawModels.filter(model => model && typeof model === 'object' && model.id) : []
    })
    const modelOptions = computed(() => store.state.llm?.modelOptions || {})
    const availableModels = computed(() => {
      if (!modelForm.provider) return []
      const opts = (modelOptions.value && modelOptions.value[modelForm.provider]) || []
      if (opts.length > 0) return opts

      // Fallback catalog for Groq when backend does not return options
      if (modelForm.provider === 'groq') {
        return [
          { name: 'LLaMA 3 70B', model_id: 'llama3-70b-8192' },
          { name: 'LLaMA 3 8B', model_id: 'llama3-8b-8192' },
          { name: 'Mixtral 8x7B', model_id: 'mixtral-8x7b-32768' },
          { name: 'Gemma 7B', model_id: 'gemma-7b-it' }
        ]
      }
      return []
    })
    
    const selectedProvider = ref(null)
    const editingModel = ref(null)
    const error = ref('')
    const success = ref('')
    const showCreateForm = ref(false)
    
    // Group models by provider
    const groupedModels = computed(() => {
      const grouped = {}
      models.value.forEach(model => {
        if (!grouped[model.provider]) {
          grouped[model.provider] = []
        }
        grouped[model.provider].push(model)
      })
      return grouped
    })
    
    // Get models for selected provider
    const selectedModels = computed(() => {
      return selectedProvider.value ? groupedModels.value[selectedProvider.value] || [] : []
    })
    
    // Form for creating/editing models
    const modelForm = reactive({
      name: '',
      provider: '',
      model_id: '',
      api_key: '',
      endpoint: '',
      max_tokens: 1000,
      temperature: 0.7,
      system_level: false,
      org_id: localStorage.getItem('orgId') // Add org_id from local storage
    })

    const fetchModels = async () => {
      try {
        await store.dispatch('llm/fetchAll')
      } catch (err) {
        console.error('Error fetching models:', err)
        error.value = 'Failed to fetch models'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const selectProvider = (provider) => {
      selectedProvider.value = provider
      editingModel.value = null
      showCreateForm.value = false
    }
    
    const editModel = (model) => {
      editingModel.value = model
      showCreateForm.value = false
      
      // Populate form with model data
      modelForm.name = model.name
      modelForm.provider = model.provider
      modelForm.model_id = model.model_id
      modelForm.api_key = model.api_key || ''
      modelForm.endpoint = model.endpoint || ''
      modelForm.max_tokens = model.max_tokens || 1000
      modelForm.temperature = model.temperature || 0.7
      modelForm.system_level = model.system_level || false
    }
    
    const resetForm = () => {
      modelForm.name = ''
      modelForm.provider = ''
      modelForm.model_id = ''
      modelForm.api_key = ''
      modelForm.endpoint = ''
      modelForm.max_tokens = 1000
      modelForm.temperature = 0.7
      modelForm.system_level = false
    }
    
    const closeForm = () => {
      showCreateForm.value = false
      editingModel.value = null
      resetForm()
    }

    const onProviderChange = async () => {
      modelForm.model_id = ''
      const provider = modelForm.provider
      if (provider) {
        try {
          // Reset API key and endpoint for local provider
          if (provider === 'local') {
            modelForm.api_key = 'not-required'
            modelForm.endpoint = 'http://localhost:11434'
          } else {
            modelForm.api_key = ''
            modelForm.endpoint = ''
          }
          
          await store.dispatch('llm/fetchModelOptions', provider)
        } catch (err) {
          console.error('Error fetching model options:', err)
          error.value = 'Failed to load model options'
          setTimeout(() => error.value = '', 3000)
        }
      }
    }

    const createModel = async () => {
      try {
        const modelData = {
          name: modelForm.name,
          provider: modelForm.provider,
          model_id: modelForm.model_id,
          api_key: modelForm.api_key,
          endpoint: modelForm.endpoint,
          max_tokens: modelForm.max_tokens,
          temperature: modelForm.temperature,
          system_level: modelForm.system_level,
          org_id: localStorage.getItem('orgId')
        }
        
        // For local provider, ensure we have the correct defaults
        if (modelForm.provider === 'local') {
          modelData.api_key = 'not-required'
          modelData.endpoint = modelData.endpoint || 'http://localhost:11434'
        }
        
        const result = await store.dispatch('llm/create', modelData)
        
        success.value = 'Model created successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchModels()
        resetForm()
        showCreateForm.value = false
        
        // Select the provider of the newly created model
        selectProvider(modelForm.provider)
      } catch (err) {
        console.error('Error creating model:', err)
        error.value = 'Failed to create model'
        setTimeout(() => error.value = '', 3000)
      }
    }
    
    const updateModel = async () => {
      if (!editingModel.value) return
      
      try {
        await store.dispatch('llm/update', {
          id: editingModel.value.id,
          updates: {
            name: modelForm.name,
            provider: modelForm.provider,
            model_id: modelForm.model_id,
            api_key: modelForm.api_key,
            endpoint: modelForm.endpoint,
            max_tokens: modelForm.max_tokens,
            temperature: modelForm.temperature,
            system_level: modelForm.system_level
          }
        })
        
        success.value = 'Model updated successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchModels()
        editingModel.value = null
        
        // Refresh the selected provider
        if (selectedProvider.value) {
          selectProvider(selectedProvider.value)
        }
      } catch (err) {
        console.error('Error updating model:', err)
        error.value = 'Failed to update model'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const deleteModel = async (modelId) => {
      if (!confirm('Are you sure you want to delete this model?')) return
      
      try {
        await store.dispatch('llm/delete', modelId)
        success.value = 'Model deleted successfully!'
        setTimeout(() => success.value = '', 3000)
        
        await fetchModels()
        
        // Refresh the selected provider
        if (selectedProvider.value) {
          selectProvider(selectedProvider.value)
        }
      } catch (err) {
        console.error('Error deleting model:', err)
        error.value = 'Failed to delete model'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const testModel = (model) => {
      // Implement model testing functionality
      console.log('Testing model:', model)
      // This could open a chat interface or run a test query
    }
    
    const getProviderName = (provider) => {
      const names = {
        'openai': 'OpenAI',
        'anthropic': 'Anthropic',
        'gemini': 'Google Gemini',
        'groq': 'Groq',
        'local': 'Local Models'
      }
      return names[provider] || provider.charAt(0).toUpperCase() + provider.slice(1)
    }
    
    const getProviderIcon = (provider) => {
      const icons = {
        'openai': 'icon-openai',
        'anthropic': 'icon-anthropic',
        'gemini': 'icon-gemini',
        'groq': 'icon-provider', // fallback; customize if a Groq icon is available
        'local': 'icon-local'
      }
      return icons[provider] || 'icon-provider'
    }
    
    const truncateEndpoint = (endpoint) => {
      if (!endpoint) return ''
      return endpoint.length > 30 ? endpoint.substring(0, 30) + '...' : endpoint
    }

    onMounted(async () => {
      await fetchModels()
    })

    return {
      models,
      groupedModels,
      selectedProvider,
      selectedModels,
      editingModel,
      error,
      success,
      showCreateForm,
      modelForm,
      availableModels,
      selectProvider,
      editModel,
      closeForm,
      onProviderChange,
      createModel,
      updateModel,
      deleteModel,
      testModel,
      getProviderName,
      getProviderIcon,
      truncateEndpoint
    }
  }
}
</script>

<style scoped>
/* Main Container */
.llms-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #ffffff;
  color: #2c3e50;
  border-bottom: 1px solid #e9ecef;
}

.page-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

/* Messages */
.error-message {
  background: linear-gradient(45deg, #ff6b6b, #ee5a52);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

.success-message {
  background: linear-gradient(45deg, #51cf66, #40c057);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(81, 207, 102, 0.3);
}

/* Main Content Layout */
.main-content {
  display: flex;
  flex: 1;
  gap: 1rem;
  padding: 1rem 1.25rem;
  overflow: hidden;
}

/* Left Panel - Providers List */
.left-panel {
  width: 300px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.panel-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.provider-count {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Providers List */
.providers-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.provider-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.provider-item:hover {
  background: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.provider-item.active {
  background: linear-gradient(45deg, #e3f2fd, #f3e5f5);
  border-color: #2196f3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}

.provider-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.provider-info {
  flex: 1;
  min-width: 0;
}

.provider-info h4 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.provider-info p {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.3;
}

.provider-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.active-count {
  background: #e8f5e8;
  color: #28a745;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Right Panel */
.right-panel {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Model Form Panel */
.model-form-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.model-form {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

input, textarea, select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.range-input {
  appearance: none;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  outline: none;
}

.range-input::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  background: #007bff;
  border-radius: 50%;
  cursor: pointer;
}

.settings-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.settings-section h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-right: 0.75rem;
  cursor: pointer;
  position: relative;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 2px solid #e9ecef;
  border-radius: 4px;
  background: white;
  transition: all 0.2s ease;
}

.checkbox-label input[type="checkbox"]:hover {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
}

.checkbox-label input[type="checkbox"]:checked {
  background: #007bff;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

.checkbox-label input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
}

.checkbox-label input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.2);
}

.form-actions {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.2s;
}

.btn-close:hover {
  color: #495057;
}

/* Models Panel */
.models-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.provider-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.model-count-badge {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.provider-overview {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.provider-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-item .label {
  font-weight: 600;
  color: #495057;
}

.stat-item .value {
  color: #6c757d;
}

.stat-item .status {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.stat-item .status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.stat-item .status.inactive {
  background: #ffebee;
  color: #c62828;
}

.models-section {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.no-models {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6c757d;
  text-align: center;
}

.no-models i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.models-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.model-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
}

.model-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-color: #007bff;
}

.model-card.active {
  border-color: #28a745;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.2);
}

.model-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.model-icon i {
  font-size: 1.5rem;
  color: #007bff;
}

.model-actions {
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.model-card:hover .model-actions {
  opacity: 1;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f8f9fa;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #e9ecef;
  color: #495057;
}

.btn-icon.danger:hover {
  background: #ffebee;
  color: #c62828;
}

.model-content {
  padding: 1rem;
}

.model-content h4 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
  font-size: 1rem;
  line-height: 1.3;
}

.model-id {
  margin: 0 0 1rem;
  color: #6c757d;
  font-size: 0.85rem;
  font-family: 'Courier New', monospace;
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  word-break: break-all;
}

.model-meta {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
}

.meta-label {
  color: #6c757d;
  font-weight: 500;
}

.meta-value {
  color: #495057;
}

.meta-value.status {
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.meta-value.status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.meta-value.status.inactive {
  background: #ffebee;
  color: #c62828;
}

.meta-value.endpoint {
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  background: #f8f9fa;
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
}

.system-badge {
  background: linear-gradient(45deg, #ffc107, #ff8f00);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
}

/* Welcome Panel */
.welcome-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.welcome-content {
  text-align: center;
  color: #6c757d;
  max-width: 500px;
}

.welcome-content i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
  color: #007bff;
}

.welcome-content h3 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.5rem;
}

.welcome-content p {
  margin: 0 0 2rem;
  line-height: 1.6;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.btn-secondary:hover {
  background: #e9ecef;
  color: #495057;
}

/* Icons */
.icon-plus:before { content: '+'; }
.icon-close:before { content: '✕'; }
.icon-edit:before { content: '✏️'; }
.icon-test:before { content: '🧪'; }
.icon-trash:before { content: '🗑️'; }
.icon-model:before { content: '🤖'; }
.icon-model-large:before { content: '🤖'; }
.icon-openai:before { content: '🤖'; }
.icon-anthropic:before { content: '🧠'; }
.icon-gemini:before { content: '💎'; }
.icon-local:before { content: '🏠'; }
.icon-provider:before { content: '⚡'; }

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    max-height: 300px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .models-grid {
    grid-template-columns: 1fr;
  }
}
</style>

